import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "untitledui-js/react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@pondigitalsolutions/pon-component-library";
import { twMerge } from "tailwind-merge";

import { Layout } from "@components/Layout";
import { languageDutch, languageEnglish } from "../i18n";
import { useDepartments } from "@hooks/useDepartments";
import { useSimpleOutsideClick } from "@hooks/useSimpleOutsideClick";
import { Loader } from "@components/Loader";
import { PageBody } from "../storyblok/PageBody";
import { DepartmentStory } from "../types/DepartmentTypes";
import { ShareButton } from "@components/ShareButton";
import { IconButton } from "@components/IconButton";
import { StackLayer } from "@components/departments/StackLayer";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { getLanguageFromPathname } from "@utils/slugUtils";
import {
  buildDepartmentHref,
  getDefaultDepartmentFolder,
  getLocalizedDepartmentFolder,
  humanizeDepartmentFolder,
  isDirectDepartmentChild,
  normalizeStoryblokPath,
  stripRouteLanguage,
} from "@utils/departmentPaths";
import { Department } from "../constants";

interface DepartmentPageProps {
  story?: DepartmentStory;
  folderPath?: string;
}

export const DepartmentPage = ({
  story: resolvedStory,
  folderPath,
}: DepartmentPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLang = useCurrentLang(
    getLanguageFromPathname(location.pathname)
  );

  // A resolved story supplies both its default folder for Storyblok queries and
  // its localized folder for display. Folder mode derives both from the URL.
  const defaultFolderPath = resolvedStory
    ? getDefaultDepartmentFolder(resolvedStory)
    : normalizeStoryblokPath(folderPath);
  const localizedFolderPath = resolvedStory
    ? getLocalizedDepartmentFolder(resolvedStory, currentLang)
    : stripRouteLanguage(normalizeStoryblokPath(folderPath), currentLang);
  const pageTitle = humanizeDepartmentFolder(localizedFolderPath);

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [nextSlideData, setNextSlideData] = useState<DepartmentStory | null>(
    null
  );
  const [activeSlideTitle, setActiveSlideTitle] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>;

  const { data, isLoading, error } = useDepartments(
    {
      content_type: Department,
      starts_with: defaultFolderPath ? `${defaultFolderPath}/` : undefined,
      language: currentLang === languageEnglish ? languageEnglish : undefined,
      sort_by: "position:asc",
      per_page: 100,
    },
    !defaultFolderPath
  );

  const departments = useMemo(() => {
    const stories = data?.storyData ?? [];

    // Folder mode has no parent_id to compare, so path comparison removes any
    // nested descendants that also matched the starts_with query.
    if (!resolvedStory) {
      return stories.filter((department) =>
        isDirectDepartmentChild(department, localizedFolderPath, currentLang)
      );
    }

    const siblings = stories.filter((department) =>
      resolvedStory.parent_id !== undefined
        ? department.parent_id === resolvedStory.parent_id
        : getDefaultDepartmentFolder(department) === defaultFolderPath
    );

    // Keep the exact routed story visible when it is a draft or has not yet
    // appeared in the collection response, then restore Storyblok position order.
    if (
      !siblings.some((department) => department.uuid === resolvedStory.uuid)
    ) {
      siblings.push(resolvedStory);
      siblings.sort(
        (first, second) => (first.position ?? 0) - (second.position ?? 0)
      );
    }

    return siblings;
  }, [
    currentLang,
    data?.storyData,
    defaultFolderPath,
    localizedFolderPath,
    resolvedStory,
  ]);

  function navigateToDepartment(department: DepartmentStory) {
    const href = buildDepartmentHref(department, currentLang);

    // GeneralPage uses the already-loaded story while its exact-story query runs,
    // preventing the page-level loader from replacing the Department card.
    void navigate(href, { state: { departmentStory: department } });
  }

  // Update the visible card immediately, then let the URL resolve the same story
  // through GeneralPage so refreshes and browser history remain content-driven.
  const navigateToIndex = (newIndex: number) => {
    if (!departments.length) return;

    // Rapid navigation should restart rather than overlap the previous transition.
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    setIsAnimating(true);
    setCurrentIndex(newIndex);
    navigateToDepartment(departments[newIndex]);

    // Simple timeout to finish animation
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);

      // Force a complete layout recalculation after animation
      requestAnimationFrame(() => {
        // Force reflow on the new active card
        const activeCard = cardRefs.current[newIndex];
        if (activeCard) {
          void activeCard.offsetHeight;
        }

        // Force a complete document reflow
        void document.body.offsetHeight;
      });

      // Clear the ref since the timeout has completed
      animationTimeoutRef.current = null;
    }, 700); // Match the CSS transition duration
  };

  function goToNextDepartment() {
    if (!departments.length) return;
    const nextIndex = (currentIndex + 1) % departments.length;
    navigateToIndex(nextIndex);
  }

  function goToPreviousDepartment() {
    if (!departments.length) return;
    const prevIndex =
      currentIndex === 0 ? departments.length - 1 : currentIndex - 1;
    navigateToIndex(prevIndex);
  }

  // Synchronize card state when the routed story changes through direct entry,
  // browser history, language switching, or completion of in-page navigation.
  useEffect(() => {
    if (resolvedStory && departments.length) {
      // Find the index of the current department
      const departmentIndex = departments.findIndex(
        (department) => department.uuid === resolvedStory.uuid
      );

      // If the department is found, set the current index and active slide title
      if (departmentIndex !== -1) {
        setCurrentIndex(departmentIndex);
        setActiveSlideTitle(departments[departmentIndex].name);
        const nextIndex = (departmentIndex + 1) % departments.length;
        setNextSlideData(departments[nextIndex]);

        setIsAnimating(false);

        // Force a reflow to ensure the card is properly sized
        const timeoutId = setTimeout(() => {
          const activeCard = cardRefs.current[departmentIndex];
          if (activeCard) {
            // Force the browser to recalculate layout
            void activeCard.offsetHeight;
          }
        }, 100);

        // Cleanup function to clear timeout on unmount or re-run
        return () => {
          clearTimeout(timeoutId);
        };
      }
    }
  }, [departments, resolvedStory]);

  // Cleanup effect to clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  // Use custom hook for handling outside clicks
  useSimpleOutsideClick(dropdownRef, handleCloseDropdown, isDropdownOpen);

  // If the data is still loading, show a loader
  if (isLoading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <p>{t("Departments.error")}</p>;
  }

  // A URL that represents a Storyblok folder has no page of its own. Once its
  // direct children are known, replace it with the first editor-ordered story.
  if (!resolvedStory && departments.length > 0) {
    return (
      <Navigate to={buildDepartmentHref(departments[0], currentLang)} replace />
    );
  }

  // If neither an exact story nor direct Department children exist, this path
  // is not a valid services-page instance.
  if (!resolvedStory) {
    return (
      <Navigate to={currentLang === languageEnglish ? "/en/404" : "/404"} />
    );
  }

  const departmentStory = resolvedStory;
  const currentDepartmentName = departmentStory.name;

  return (
    <Layout
      translatedSlug={
        currentLang === languageDutch
          ? buildDepartmentHref(departmentStory, languageEnglish)
          : buildDepartmentHref(departmentStory, languageDutch)
      }
      className="bg-secondary-900 py-10 dark:bg-transparent"
    >
      {error && <p>{t("Departments.error")}</p>}
      {!isLoading && !error && (
        <>
          <title>{activeSlideTitle}</title>
          <div className="mx-auto max-w-360 md:px-4 2xl:px-0">
            <div className="grid grid-cols-12 gap-y-6 lg:mt-9 lg:gap-x-4">
              <div className="col-span-12 lg:col-span-3">
                <div className="lg:sticky lg:top-16">
                  <Typography
                    as="h1"
                    size="3xl"
                    className="mb-10 text-center leading-[100%] font-semibold text-white lg:mt-4 lg:mb-4 lg:text-left lg:text-2xl"
                  >
                    {pageTitle}
                  </Typography>

                  {/* Mobile dropdown menu */}
                  <div
                    className="relative px-4 md:px-0 lg:hidden"
                    ref={dropdownRef}
                  >
                    <Button
                      id="departments-dropdown-button"
                      size="md"
                      variant="soft"
                      className="flex h-12 w-full items-center justify-between rounded-lg border border-white/10 bg-secondary-700 px-4 text-white hover:border-primary-600 focus:border-secondary-400 focus:bg-secondary-700 focus:outline-none active:bg-secondary-700 dark:bg-secondary-900 dark:focus:dark:bg-secondary-900"
                      onClick={() => setIsDropdownOpen((prev) => !prev)}
                      endIcon="ChevronDown"
                      aria-label={t("Departments.selectDepartment")}
                      aria-expanded={isDropdownOpen}
                      aria-haspopup="true"
                      aria-controls="departments-dropdown-menu"
                    >
                      <span className="truncate">{currentDepartmentName}</span>
                    </Button>
                    {isDropdownOpen && (
                      <div
                        id="departments-dropdown-menu"
                        className="absolute z-20 mt-2 mr-4 rounded-lg border border-white/10 bg-secondary-700 p-2 shadow-xl dark:bg-secondary-900"
                        role="menu"
                        aria-label={pageTitle}
                      >
                        {departments.map((department) => {
                          const isSelected =
                            departmentStory.uuid === department.uuid;
                          return (
                            <button
                              key={department.uuid}
                              id={`dropdown-department-${department.uuid}`}
                              type="button"
                              className={twMerge(
                                "my-1 w-full rounded-lg border border-transparent px-4 py-1 text-left transition-colors focus:border-secondary-400 focus:outline-none",
                                isSelected
                                  ? "font-semibold text-white"
                                  : "text-white/70 hover:border-primary-600 hover:text-white dark:hover:border-primary-600"
                              )}
                              onClick={() => {
                                navigateToDepartment(department);
                                setIsDropdownOpen(false);
                              }}
                              role="menuitem"
                              aria-current={isSelected ? "true" : undefined}
                            >
                              {department.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Pages overview */}
                  <div className="hidden gap-2 px-4 md:px-0 lg:flex lg:flex-col lg:overflow-visible">
                    {departments.map((department) => (
                      <Button
                        key={department.uuid}
                        id={`department-${department.name}`}
                        size="md"
                        variant={
                          departmentStory.uuid === department.uuid
                            ? "primary"
                            : "soft"
                        }
                        className="min-h-10 w-fit max-w-full rounded-lg px-4 py-2 text-left whitespace-normal"
                        onClick={() => {
                          const departmentIndex = departments.findIndex(
                            (dept) => dept.uuid === department.uuid
                          );
                          if (departmentIndex !== -1) {
                            navigateToIndex(departmentIndex);
                          }
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        ariaLabel={t("Departments.navigateToDepartment", {
                          department: department.name,
                        })}
                      >
                        {department.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Page content */}
              <div className="col-span-12 py-8 lg:col-span-8 lg:pt-4 lg:pb-16">
                <div className="relative">
                  {/* Render only the active card */}
                  {departments.map((department, index) => {
                    const isActive = currentIndex === index;

                    // Only render the active card
                    if (!isActive) return null;

                    return (
                      <div
                        key={department.uuid}
                        className={`relative transform-gpu transition-opacity duration-700 ease-in-out ${isAnimating ? "pointer-events-none" : ""} `}
                      >
                        {/* Fake card stack layers - positioned absolutely relative to this card */}
                        <StackLayer position="-top-8" opacity={10} zIndex={1} />
                        <StackLayer position="-top-4" opacity={20} zIndex={2} />
                        <StackLayer
                          position="-bottom-4"
                          opacity={10}
                          zIndex={1}
                        />
                        <StackLayer
                          position="-bottom-8"
                          opacity={20}
                          zIndex={2}
                        />

                        <div
                          ref={(el) => {
                            cardRefs.current[index] = el;
                          }}
                          className="relative z-[3] flex flex-col overflow-hidden rounded-3xl bg-tertiary-100 shadow-2xl dark:bg-secondary-900"
                        >
                          <img
                            src={department.content.image?.filename}
                            alt={department.content.image?.alt}
                            className="h-45 w-full object-cover"
                          />
                          <div className="flex flex-1 flex-col px-4 py-8 lg:px-8 lg:py-14">
                            <div className="flex items-center justify-between">
                              <Typography
                                as="h2"
                                size="2xl"
                                className="leading-[120%] font-semibold text-secondary-700 lg:text-3xl dark:text-white"
                              >
                                {department.name}
                              </Typography>

                              <ShareButton tabIndex={isActive ? 0 : -1} />
                            </div>

                            {department.content.links &&
                              department.content.links.length > 0 && (
                                <div className="my-6 flex flex-row gap-3">
                                  {department.content.links.map(
                                    (linkItem, linkIndex) => (
                                      <Button
                                        key={linkItem._uid}
                                        id={`link-${department.uuid}-${linkIndex}`}
                                        variant={
                                          linkIndex === 0
                                            ? "primary"
                                            : "secondary"
                                        }
                                        size="xl"
                                        href={linkItem.link.cached_url}
                                        target={
                                          linkItem.link.target ?? undefined
                                        }
                                        className="w-fit gap-1"
                                        endIcon={
                                          linkItem.link.linktype === "url"
                                            ? "LinkExternal02"
                                            : undefined
                                        }
                                        tabIndex={isActive ? 0 : -1}
                                      >
                                        {linkItem.title}
                                      </Button>
                                    )
                                  )}
                                </div>
                              )}
                            <PageBody
                              blok={department.content.body}
                              className="[&_[data-component=contact-person]]:bg-transparent [&_[data-component=contact-person]]:p-0 [&>.richText]:max-w-full"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pagination */}
              <div className="col-span-12 flex flex-col items-center justify-center gap-3 lg:col-span-1 lg:mt-4 lg:items-start lg:justify-start">
                <div className="flex flex-col items-center gap-3 lg:sticky lg:top-16 lg:items-start">
                  <IconButton
                    onClick={() => {
                      goToPreviousDepartment();
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    ariaLabel={t("Departments.previous")}
                    variant="lg"
                    id="previous-department-button"
                    icon={<ChevronUp className="stroke-white" />}
                    className="hidden lg:flex"
                  />

                  <div className="flex flex-col items-center gap-2.5">
                    <IconButton
                      onClick={() => {
                        goToNextDepartment();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      ariaLabel={t("Departments.next")}
                      variant="lg"
                      id="next-department-button"
                      icon={<ChevronDown className="stroke-white" />}
                    />

                    <Typography className="text-xs font-normal text-white/80 lg:hidden">
                      {t("Departments.nextDepartment", {
                        department: nextSlideData?.name,
                      })}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Layout>
  );
};
